import React, { FunctionComponent } from "react";
import { useHistory, useParams } from "react-router-dom";
import { MepList } from "../mep";
import { useContentStyles } from "./useContentStyles";
import {Box, Grid} from "@material-ui/core";
import MepGroupFilter from "../mep/MepGroupFilter";
import MepTopFilter from "../mep/MepTopFilter";
import classNames from "classnames";
import { useTranslate } from "../../service/translation";
import { FiltersContext } from "../../service/filters";
import { ALL_GROUPS, buildChannelsPath, DEFAULT_SECTION, slugToEparty, slugToSection }
    from "../../service/filters/channelsPath";

interface ChannelsContentProps {
}

const ChannelsContent: FunctionComponent<ChannelsContentProps> = (props: ChannelsContentProps) => {
    const classes = useContentStyles();
    const translate = useTranslate();
    const history = useHistory();
    const { section, group } = useParams<{ section?: string; group?: string }>();
    const [filters, setFilters] = React.useContext(FiltersContext);

    /**
     * The URL drives the filters, and only in that direction. The tabs and pills push a new path
     * when clicked (see MepTopFilter and MepGroupFilter); this effect reads whatever path resulted
     * and brings the filters to match. Writing filters here and pushing paths there keeps one
     * direction of truth per tick, so the round trip settles after one pass instead of ping-ponging.
     *
     * The comparison before setFilters is what makes that true: once the filters already say what
     * the URL says, this is a no-op and the effect stops.
     */
    // What the URL asks for, computed during render because a child's effects run before its
    // parent's: MepList would otherwise fire a request with the default filters before the effect
    // below could apply the URL, and the two responses would race - which is how arriving at
    // /channels/committees via a redirect showed all 727 contributors while going there directly
    // showed 1.
    const wantedType = slugToSection(section);
    const wantedEParty = slugToEparty(group);
    const inSync = filters.channels.type === wantedType && filters.channels.eParty === wantedEParty;

    React.useEffect(() => {
        const type = slugToSection(section);
        const eParty = slugToEparty(group);

        // An unknown section is not an error page - /channels is a real, working page, so fall back
        // to it rather than dead-ending someone who followed a stale link.
        if (type === undefined) {
            history.replace("/channels");
            return;
        }
        // A group that no longer exists, or a group on a section that has none, loses just that
        // segment. The section the reader asked for still works.
        if (group !== undefined && (eParty === undefined || type !== "mep")) {
            history.replace(buildChannelsPath(type));
            return;
        }
        // Canonical form has no trailing slash and omits the default section, so /channels/all/ and
        // /channels/ resolve to /channels rather than becoming second URLs for one page.
        const canonical = buildChannelsPath(type, eParty);
        if (canonical !== history.location.pathname.replace(/\/+$/, "")) {
            history.replace(canonical);
            return;
        }

        if (filters.channels.type !== type || filters.channels.eParty !== eParty) {
            setFilters({
                ...filters,
                channels: { ...filters.channels, type, eParty },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [section, group]);

    return (
        <Grid container className={classes.page} id="main-content">


            <Grid container className={classes.topFilterContainer}>
                <Grid container className={classNames(classes.topFilterWrapper, 'relative')}>
                    {/*<Box className={classNames('joyrideChannels00')} sx={{ display: { xs: 'none', md: 'block' }}}></Box>
                    <Box className={classNames('joyrideChannelsXs00')} sx={{ display: { xs: 'block', md: 'none' }}}></Box>*/}
                    <MepTopFilter />
                </Grid>
            </Grid>
            <Grid container className={classNames(classes.pageWrapper, 'dblockImp')}>
                <Grid container className={classNames(classes.topFilterContainer, 'bgTrans')}>
                    <Grid container className={classes.topFilterWrapper}>
                        <MepGroupFilter />
                    </Grid>
                </Grid>
                {/* Held back until the filters agree with the URL, so the list makes one
                    request, for the right thing. */}
                {inSync ? <MepList /> : null}
            </Grid>
        </Grid>
    );
};

export default ChannelsContent;
